import { Injectable } from '@angular/core';
import { FormGroup, FormControl, Validators, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { CreateCaseRequestModel } from 'app/models/create-case-request.model';
import { CaseFormModel } from 'app/models/case-form.model';
import { SaveCaseRequestModel } from 'app/models/save-case-request.model';
import { UserService } from 'app/users/user.service';
import { FormConstants } from 'app/constants/form.constants';
import { fieldPatterns } from 'app/utilities/form.utilities';
import { FormValidator } from 'app/ui-components/form/form-validators/form-validators';
import { FormService } from './form.service';

export type RetrieveCaseResponseJson = {
  result: CreateCaseRequestModel;
}

@Injectable({
  providedIn: 'root'
})
export class FormFactoryService {
  // For readability in the validators
  private FORM_A = 'FormA';
  private FORM_B = 'FormB';
  private FORM_C = 'FormC';
  private FORM_D = 'FormD';
  private FORM_E = 'FormE';
  private FORM_F = 'FormF';

  constructor(protected userService: UserService, protected formService: FormService, protected formValidator: FormValidator) {}
  
  public createCaseFormGroup(model?: CaseFormModel, formType?: string): FormGroup {
    if(!model && !formType) {
      throw new Error("Either an existing case or a specified form type is required to create a new form.");
    }

    if(model && !formType) {
      formType = model.u_form_type;
    }

    if(formType) {
      let mappedFormType = FormConstants.SERVICE_NOW_FORM_TYPE_MAP.get(formType);
      if(mappedFormType) {
        formType = mappedFormType;
      }
    }
    
    if(!model) {
      model = new CaseFormModel();
    }

    if(!formType) {
      throw new Error("No form type specified to create.");
    }

    var applicant = model?.u_applicant ? model.u_applicant : this.userService.profile.orgName;
    
    // Won't happen, but here to prevent errors.
    if(!applicant) {
      applicant = '';
    }

    const formGroup = new FormGroup({
      caseSysId: new FormControl<string>(model.caseSysId, []),
      caseNumber: new FormControl<string>(model.caseNumber, []),
      sysId: new FormControl<string>(model.sys_id, []),
      lockedById: new FormControl<string>(model.u_locked_by_sso_id, []),
      lastLockedTime: new FormControl<string>(model.u_last_locked_activity_time, []),
      number: new FormControl<string>(model.number, []),
      trademarks: new FormControl<string>(model.u_trademarks, Validators.required),
      manufacturers: new FormControl<string>(model.u_manufacturers, Validators.required),
      shortDescription: new FormControl<string>(model.short_description, []),
      description: new FormControl<string>(model.description, []),
      urgency: new FormControl<string>(model.urgency, []),
      submittedByFirstName: new FormControl<string>(model.u_submitted_by_first_name, []),
      submittedByLastName: new FormControl<string>(model.u_submitted_by_last_name, []),
      applicantInfo: new FormGroup({
        applicant: new FormControl<string>(applicant, [Validators.required]),
        street1: new FormControl<string>(model.u_street_1, [Validators.required]),
        street2: new FormControl<string>(model.u_street_2, []),
        city: new FormControl<string>(model.u_city, [Validators.required]),
        state: new FormControl<string>(model.u_state, []),
        postalCode: new FormControl<string>(model.u_postal_code, []),
        country: new FormControl<string>(model.u_country, [Validators.required]),
        telephoneNumber: new FormControl<string>(model.u_telephone_number, [Validators.required, Validators.pattern(fieldPatterns.telephoneNumber)]),
        telephoneType: new FormControl<string>(model.u_telephone_type, Validators.required),
        primaryContactFirstName: new FormControl<string>(model.u_primary_contact_first_name, [Validators.required]),
        primaryContactLastName: new FormControl<string>(model.u_primary_contact_last_name, [Validators.required]),
        pronouns: new FormControl<string>(model.u_pronoun, []),
        title: new FormControl<string>(model.u_title, []),
        emailAddress: new FormControl<string>(model.u_email_address, [Validators.required, Validators.email])
      }),
      collaborators: new FormGroup({
        collaborator1: new FormGroup({
          ssoId: new FormControl<string>(model.u_usan_collaborator_1_sso_id, []),
          firstName: new FormControl<string>(model.u_usan_collaborator_1_first_name, []),
          lastName: new FormControl<string>(model.u_usan_collaborator_1_last_name, []),
          email: new FormControl<string>(model.u_usan_collaborator_1_email, []),
        }),
        collaborator2: new FormGroup({
          ssoId: new FormControl<string>(model.u_usan_collaborator_2_sso_id, []),
          firstName: new FormControl<string>(model.u_usan_collaborator_2_first_name, []),
          lastName: new FormControl<string>(model.u_usan_collaborator_2_last_name, []),
          email: new FormControl<string>(model.u_usan_collaborator_2_email, []),
        }),
      }),
      usanOrgId: new FormControl<string>(model.u_usan_org_id, []),
      caseStatus: new FormControl<string>(model.u_case_status, []),
      molecularFormulaForSingleEntity: new FormControl<string>(model.u_molecular_formula_for_single_entity, []),
      suggestedNamesFormA: new FormControl<string[]>(this.splitStringToArray(model.u_suggested_names_in_order_of_preference_for_single_entity), FormValidator.isRequiredForFormType(formType, [this.FORM_A, this.FORM_B, this.FORM_C, this.FORM_E, this.FORM_F])), 
      designationForSaltForm: new FormControl<string>(model.u_designation_for_salt_form, FormValidator.isRequiredForFormType(formType, [this.FORM_A])),
      chemicalNamesDescription: new FormControl<string>(model.u_chemical_names_or_description, FormValidator.isRequiredForFormType(formType, [this.FORM_B, this.FORM_C, this.FORM_D, this.FORM_E])),
      systematicNamesOrDescription: new FormControl<string>(model.u_systematic_names_or_description, FormValidator.isRequiredForFormType(formType, [this.FORM_F])),
      chemicalNamesDescriptionForSingleEntity: new FormControl<string>(model.u_chemical_names_or_description_for_single_entity, FormValidator.isRequiredForFormType(formType, [this.FORM_A])),
      chemicalNamesDescriptionForSaltForm: new FormControl<string>(model.u_chemical_names_or_description_for_salt_form, FormValidator.isRequiredForFormType(formType, [this.FORM_A])),
      molecularWeightForSingleEntity: new FormControl<string>(model.u_molecular_weight_for_single_entity, [FormValidator.isRequiredForFormType(formType, [this.FORM_A]), Validators.pattern(fieldPatterns.molecularFormula)]),
      molecularWeightForSaltForm: new FormControl<string>(model.u_molecular_weight_for_salt_form, [FormValidator.isRequiredForFormType(formType, [this.FORM_A]), Validators.pattern(fieldPatterns.molecularFormula)]),
      casRegistryNumberForSingleEntity: new FormControl<string>(model.u_chemical_abstracts_service_cas_registry_number_for_single_entity, [FormValidator.isRequiredForFormType(formType, [this.FORM_A]), Validators.pattern(fieldPatterns.molecularFormula)]),
      casRegistryNumberForSaltForm: new FormControl<string>(model.u_chemical_abstracts_service_cas_registry_number_for_salt_form, [FormValidator.isRequiredForFormType(formType, [this.FORM_A]), Validators.pattern(fieldPatterns.molecularFormula)]),
      uniiNumberforSingleEntity: new FormControl<string>(model.u_unique_ingredient_identifier_unii_number, [Validators.pattern(/^(not\savailable)|(n\/a)|(n\\a)|(unknown)|[a-zA-Z0-9]+$/i)]),
      codeDesignationsForSingleEntity: new FormControl<string>(model.u_code_designation_for_single_entity, FormValidator.isRequiredForFormType(formType, [this.FORM_A])),
      codeDesignationsForSaltForm: new FormControl<string>(model.u_code_designation_for_salt_form, FormValidator.isRequiredForFormType(formType, [this.FORM_A])),
      trivialNames: new FormControl<string>(model.u_trivial_names, []),
      principalTherapeuticUses: new FormControl<string>(model.u_principal_theraputic_uses, FormValidator.isRequiredForFormType(formType, [this.FORM_A, this.FORM_B, this.FORM_C, this.FORM_F])),
      pharmacologicAction: new FormControl<string>(model.u_pharmacologic_action, FormValidator.isRequiredForFormType(formType, [this.FORM_A, this.FORM_B, this.FORM_F])),
      substanceBindsToTarget: new FormControl<string>(model.u_does_the_substance_bind_to_a_receptor_enzyme_or_other_target, FormValidator.isRequiredForFormType(formType, [this.FORM_A, this.FORM_B, this.FORM_F])),
      substanceBindingInfo: new FormControl<string>(model.u_substance_bind_explanation, []),
      innSubmission: new FormControl<string>(model.u_inn_submission, FormValidator.isRequiredForFormType(formType, [this.FORM_A, this.FORM_B, this.FORM_F])),
      innSubmissionNumber: new FormControl<string>(model.u_inn_submission_number, []), 
      innExplanation: new FormControl<string>(model.u_inn_submission_explanation, []), 
      phase1ClinicalTrialStartDate: new FormControl<string>(model.u_phase_1_clinical_trials_began_date, [FormValidator.isRequiredForFormType(formType, [this.FORM_A, this.FORM_B, this.FORM_C, this.FORM_D, this.FORM_F])]),
      indApplicationNumbers: new FormControl<string>(model.u_ind_application_numbers, FormValidator.isRequiredForFormType(formType, [this.FORM_A, this.FORM_B, this.FORM_C, this.FORM_D, this.FORM_F])),
      caseType: new FormControl<string>(model.u_case_type, []),
      revisionRequestName: new FormControl<string>(model.u_name_for_which_you_are_requesting_a_revision, []),
      revisionReason: new FormControl<string>(model.u_reason_for_revision, []),
      molecularFormulaForSaltForm: new FormControl<string>(model.u_molecular_formula_for_salt_form, []),
      molecularFormula: new FormControl<string>(model.u_molecular_formula, []),
      molecularWeight: new FormControl<string>(model.u_molecular_weight, [FormValidator.isRequiredForFormType(formType, [this.FORM_B, this.FORM_C, this.FORM_D, this.FORM_F]), Validators.pattern(fieldPatterns.molecularFormula)]),
      isProDrug: new FormControl<string>(model.u_is_this_a_pro_drug, []),
      casRegistryNumber: new FormControl<string>(model.u_chemical_abstracts_service_cas_registry_number, [FormValidator.isRequiredForFormType(formType, [this.FORM_B, this.FORM_C, this.FORM_D, this.FORM_E, this.FORM_F]), Validators.pattern(fieldPatterns.molecularFormula)]),
      uniiNumberForSaltForm: new FormControl<string>(model.u_unique_ingredient_identifier_unii_number_salt, [Validators.pattern(/^(not\savailable)|(n\/a)|(n\\a)|(unknown)|[a-zA-Z0-9]+$/i)]), 
      therapClaimInd: new FormControl<string>(model.u_indications_therapeutic_claim, []),
      codeDesignations: new FormControl<string>(model.u_code_designations, FormValidator.isRequiredForFormType(formType, [this.FORM_B, this.FORM_C, this.FORM_D, this.FORM_F])),
      address: new FormControl<string>(model.u_address, [Validators.pattern(/^\d*$/)]),
      signature: new FormControl<string>(model.u_signature, []),
      formType: new FormControl<string>(formType, []),
      fileNumberForSingleEntity: new FormControl<string>(model.u_file_number_single_entity, []),
      fileNumberModified: new FormControl<string>(model.u_file_number_modified, []),
      acknowledged: new FormControl<string>(model.u_acknowledged, []),
      whoNumber: new FormControl<string>(model.u_who_number, []),
      innStatus: new FormControl<string>(model.u_inn_status, []),
      monomerAndPolymerFormulas: new FormControl<string>(model.u_list_the_formula_for_each_monomer_and_for_the_whole_polymer, []),
      chemicalsAttached: new FormControl<string>(model.u_chemicals_attached, []),
      manufacturingProcess: new FormControl<string>(model.u_manufacturing_process, []),
      purityOf2HydroxyethylMethacrylate: new FormControl<string>(model.u_purity_of_2_hydroxyethyl_methacrylate_if_applicable, []),
      waterContentAtAmbientTemperatures: new FormControl<string>(model.u_water_content_at_ambient_temperature_23_2_c, []),
      waterContent: new FormControl<string>(model.u_water_content, []),
      waterContentMeanValueStdDeviation: new FormControl<string>(model.u_mean_value_standard_deviation_water_content, [Validators.pattern(/^\d*\.\d+$|^\d+$/)]),
      waterContentNumberOfMeasurements: new FormControl<string>(model.u_number_of_measurements_water_content, [Validators.pattern(/^\d*$/)]),
      oxygenPermeabilityAt35C: new FormControl<string>(model.u_oxygen_permeability_at_35_c, []),
      oxygenPermeability: new FormControl<string>(model.u_oxygen_permeability, []),
      oxygenMeanValueStdDeviation: new FormControl<string>(model.u_mean_value_standard_deviation_oxygen, [Validators.pattern(/^\d*\.\d+$|^\d+$/)]),
      oxygenNumberOfMeasurements: new FormControl<string>(model.u_number_of_measurements_oxygen, [Validators.pattern(/^\d*$/)]),
      methodUsedToDetermineOxygenPermeability: new FormControl<string>(model.u_method_used_to_determine_oxygen_permeability, []),
      usanUserSsoId: new FormControl<string>(model.u_usan_user_sso_id, []),
      usanUserFirstName: new FormControl<string>(model.u_usan_user_firstname, []),
      usanUserLastName: new FormControl<string>(model.u_usan_user_lastname, []),
      usanUserEmail: new FormControl<string>(model.u_usan_user_email, []),
      usanUserApplicantSsoId: new FormControl<string>(model.u_usan_applicant_sso_id, []),
      usanUserApplicantFirstName: new FormControl<string>(model.u_usan_applicant_firstname, []),
      usanUserApplicantLastName: new FormControl<string>(model.u_usan_applicant_lastname, []),
      usanUserApplicantEmail: new FormControl<string>(model.u_usan_applicant_email, []),
      polymerPresenceDisclosure: new FormControl<string>(model.u_please_disclose_if_there_is_a_polymer_present, []),
      semiInterpenetratingNetworkDisclosure: new FormControl<string>(model.u_please_disclose_whether_a_semi_interpenetrating_network_is_formed, []),
      degreeOfPolymerizationDisclosure: new FormControl<string>(model.u_please_disclose_the_degreee_of_polymerization, []),
      paymentType: new FormControl<string>(model.u_payment_type, []),
      paymentStatus: new FormControl<string>(model.u_payment_status, []),
      paymentAmount: new FormControl<number>(model.u_payment_amount, []),
      paymentDate: new FormControl<string>(model.u_payment_date, []),
      orderId: new FormControl<string>(model.u_order_id, []),
      transactionId: new FormControl<string>(model.u_trans_id, []),
      authCode: new FormControl<string>(model.u_auth_cd, []),
      submittedDate: new FormControl<string>(model.u_application_submitted_date, []),
    });

    let applicantInfoFG = formGroup.get('applicantInfo');
    if(applicantInfoFG) {
      this.formValidator.addOrRemoveCountrySpecificValidation(applicantInfoFG);
    }
    return formGroup;

  }

  public createCreateCaseRequestModel(formGroup: FormGroup): CreateCaseRequestModel {
    const values = {
      // Map any custom values specific to the CreateCaseRequestModel here.
    };

    let caseFormModel = CaseFormModel.parseValues(this.createCaseFormModel(formGroup));
    Object.assign(values, caseFormModel);
    return new CreateCaseRequestModel(values);
  }

  public createSaveCaseRequestModel(formGroup: FormGroup): SaveCaseRequestModel {
    const values = {
      // Map any custom values specific to the CreateCaseRequestModel here.
    };

    let caseFormModel = CaseFormModel.parseValues(this.createCaseFormModel(formGroup));
    Object.assign(values, caseFormModel);
    return new SaveCaseRequestModel(values);
  }

  private createCaseFormModel(formGroup: FormGroup): CaseFormModel {
    const caseStatus = formGroup.get('caseStatus')?.value ? formGroup.get('caseStatus')?.value : "Draft"; // Set to "Draft" if there isn't already a status.
   
    let formTypeParam = formGroup.get('formType')?.value;
    let formType = FormConstants.SERVICE_NOW_FORM_TYPE_MAP.get(formTypeParam);
    
    if(!formType) {
      formType = formTypeParam;
    }
    
    const applicantInfo = formGroup.get('applicantInfo') as FormGroup;
    
    const orgName = this.userService.profile.orgName;
    const values = {
      caseSysId: formGroup.get('caseSysId')?.value,
      caseNumber: formGroup.get('caseNumber')?.value,
      sys_id: formGroup.get('sysId')?.value,
      u_locked_by_sso_id: formGroup.get('lockedById')?.value,
      u_last_locked_activity_time: formGroup.get('lastLockedTime')?.value,
      u_manufacturers: formGroup.get('manufacturers')?.value,
      short_description: formGroup.get('shortDescription')?.value,
      description: formGroup.get('description')?.value,
      urgency: formGroup.get('urgency')?.value,
      u_submitted_by_first_name: formGroup.get('submittedByFirstName')?.value,
      u_submitted_by_last_name: formGroup.get('submittedByLastName')?.value,
      u_usan_org_id: formGroup.get('usanOrgId')?.value,
      u_case_status: caseStatus,
      u_molecular_formula_for_single_entity: formGroup.get('molecularFormulaForSingleEntity')?.value,
      u_suggested_names_in_order_of_preference_for_single_entity: this.mergeArrayToString(formGroup.get('suggestedNamesFormA')?.value),
      u_designation_for_salt_form: formGroup.get('designationForSaltForm')?.value,
      u_chemical_names_or_description: formGroup.get('chemicalNamesDescription')?.value,
      u_systematic_names_or_description: formGroup.get('systematicNamesOrDescription')?.value,
      u_chemical_names_or_description_for_single_entity: formGroup.get('chemicalNamesDescriptionForSingleEntity')?.value,
      u_chemical_names_or_description_for_salt_form: formGroup.get('chemicalNamesDescriptionForSaltForm')?.value,
      u_molecular_weight_for_single_entity: formGroup.get('molecularWeightForSingleEntity')?.value,
      u_molecular_weight_for_salt_form: formGroup.get('molecularWeightForSaltForm')?.value,
      u_chemical_abstracts_service_cas_registry_number_for_single_entity: formGroup.get('casRegistryNumberForSingleEntity')?.value,
      u_chemical_abstracts_service_cas_registry_number_for_salt_form: formGroup.get('casRegistryNumberForSaltForm')?.value,
      u_unique_ingredient_identifier_unii_number: formGroup.get('uniiNumberforSingleEntity')?.value,
      u_code_designation_for_single_entity: formGroup.get('codeDesignationsForSingleEntity')?.value,
      u_code_designation_for_salt_form: formGroup.get('codeDesignationsForSaltForm')?.value,
      u_trivial_names: formGroup.get('trivialNames')?.value,
      u_principal_theraputic_uses: formGroup.get('principalTherapeuticUses')?.value,
      u_pharmacologic_action: formGroup.get('pharmacologicAction')?.value,
      u_does_the_substance_bind_to_a_receptor_enzyme_or_other_target: formGroup.get('substanceBindsToTarget')?.value,
      u_substance_bind_explanation: formGroup.get('substanceBindingInfo')?.value,
      u_inn_submission: formGroup.get('innSubmission')?.value,
      u_inn_submission_number: formGroup.get('innSubmissionNumber')?.value,
      u_inn_submission_explanation: formGroup.get('innExplanation')?.value,
      u_phase_1_clinical_trials_began_date: formGroup.get('phase1ClinicalTrialStartDate')?.value,
      u_ind_application_numbers: formGroup.get('indApplicationNumbers')?.value,
      u_case_type: formGroup.get('caseType')?.value,
      u_name_for_which_you_are_requesting_a_revision: formGroup.get('revisionRequestName')?.value,
      u_reason_for_revision: formGroup.get('revisionReason')?.value,
      u_molecular_formula_for_salt_form: formGroup.get('molecularFormulaForSaltForm')?.value,
      u_molecular_formula: formGroup.get('molecularFormula')?.value,
      u_molecular_weight: formGroup.get('molecularWeight')?.value,
      u_is_this_a_pro_drug: formGroup.get('isProDrug')?.value,
      u_chemical_abstracts_service_cas_registry_number: formGroup.get('casRegistryNumber')?.value,
      u_unique_ingredient_identifier_unii_number_salt: formGroup.get('uniiNumberForSaltForm')?.value,
      u_indications_therapeutic_claim: formGroup.get('therapClaimInd')?.value,
      u_code_designations: formGroup.get('codeDesignations')?.value,
      u_address: formGroup.get('address')?.value,
      u_signature: formGroup.get('signature')?.value,
      u_form_type: formType,
      u_file_number_single_entity: formGroup.get('fileNumberForSingleEntity')?.value,
      u_file_number_modified: formGroup.get('fileNumberModified')?.value,
      u_acknowledged: formGroup.get('acknowledged')?.value,
      u_who_number: formGroup.get('whoNumber')?.value,
      u_inn_status: formGroup.get('innStatus')?.value,
      u_list_the_formula_for_each_monomer_and_for_the_whole_polymer: formGroup.get('monomerAndPolymerFormulas')?.value,
      u_chemicals_attached: formGroup.get('chemicalsAttached')?.value,
      u_manufacturing_process: formGroup.get('manufacturingProcess')?.value,
      u_purity_of_2_hydroxyethyl_methacrylate_if_applicable: formGroup.get('purityOf2HydroxyethylMethacrylate')?.value,
      u_water_content_at_ambient_temperature_23_2_c: formGroup.get('waterContentAtAmbientTemperatures')?.value,
      u_water_content: formGroup.get('waterContent')?.value,
      u_mean_value_standard_deviation_water_content: formGroup.get('waterContentMeanValueStdDeviation')?.value,
      u_number_of_measurements_water_content: formGroup.get('waterContentNumberOfMeasurements')?.value,
      u_oxygen_permeability_at_35_c: formGroup.get('oxygenPermeabilityAt35C')?.value,
      u_oxygen_permeability: formGroup.get('oxygenPermeability')?.value,
      u_mean_value_standard_deviation_oxygen: formGroup.get('oxygenMeanValueStdDeviation')?.value,
      u_number_of_measurements_oxygen: formGroup.get('oxygenNumberOfMeasurements')?.value,
      u_method_used_to_determine_oxygen_permeability: formGroup.get('methodUsedToDetermineOxygenPermeability')?.value,
      u_usan_user_sso_id: formGroup.get('usanUserSsoId')?.value,
      u_usan_user_firstname: formGroup.get('usanUserFirstName')?.value,
      u_usan_user_lastname: formGroup.get('usanUserLastName')?.value,
      u_usan_user_email: formGroup.get('usanUserEmail')?.value,
      u_please_disclose_if_there_is_a_polymer_present: formGroup.get('polymerPresenceDisclosure')?.value,
      u_please_disclose_whether_a_semi_interpenetrating_network_is_formed: formGroup.get('semiInterpenetratingNetworkDisclosure')?.value,
      u_please_disclose_the_degreee_of_polymerization: formGroup.get('degreeOfPolymerizationDisclosure')?.value,
      u_applicant: applicantInfo.get('applicant')?.value && applicantInfo.get('applicant')?.value != '' ? applicantInfo.get('applicant')?.value : orgName,
      u_street_1: applicantInfo.get('street1')?.value,
      u_street_2: applicantInfo.get('street2')?.value,
      u_city: applicantInfo.get('city')?.value,
      u_state: applicantInfo.get('state')?.value,
      u_postal_code: applicantInfo.get('postalCode')?.value,
      u_country: applicantInfo.get('country')?.value,
      u_telephone_number: applicantInfo.get('telephoneNumber')?.value,
      u_telephone_type: applicantInfo.get('telephoneType')?.value,
      u_email_address: applicantInfo.get('emailAddress')?.value,
      u_title: applicantInfo.get('title')?.value,
      u_primary_contact_first_name: applicantInfo.get('primaryContactFirstName')?.value,
      u_primary_contact_last_name: applicantInfo.get('primaryContactLastName')?.value,
      u_pronoun: applicantInfo.get('pronouns')?.value,
      u_trademarks: formGroup.get('trademarks')?.value,
      u_payment_type: formGroup.get('paymentType')?.value,
      u_payment_status: formGroup.get('paymentStatus')?.value,
      u_payment_amount: formGroup.get('paymentAmount')?.value,
      u_payment_date: formGroup.get('paymentDate')?.value,
      u_order_id: formGroup.get('orderId')?.value,
      u_trans_id: formGroup.get('transactionId')?.value,
      u_auth_cd: formGroup.get('authCode')?.value,
      u_application_submitted_date: formGroup.get('submittedDate')?.value
    };

    if (this.userService.profile.orgId && !formGroup.get('usanUserApplicantSsoId')?.value) {
      const profileInfo = {
        u_usan_org_id: this.userService.profile.orgId ?? '',
        u_usan_applicant_sso_id: this.userService.profile.uniqueId ?? '',
        u_usan_applicant_firstname: this.userService.profile.firstName ?? '',
        u_usan_applicant_lastname: this.userService.profile.lastName ?? '',
        u_usan_applicant_email: this.userService.profile.email ?? ''
      };
      Object.assign(values, profileInfo);
    }

    return CaseFormModel.parseValues(values);
  }

  private splitStringToArray(str: string): string[] {
    if(!str) {
      return new Array<string>(); 
    }
    
    let array = str.split(',').map(function(s) {
      return s.trim();
    });

    return array;
  }

  private mergeArrayToString(array: string[]): string {
    let merged = '';
    if(!array || array.length < 1) {
      return merged;
    }
    let counter = 1;
    array.forEach(function(str) {
      merged += str;
      if(counter < array.length) {
        merged += ', ';
      }
      counter++;
    });

    return merged;
  }

}
